import { useEffect, useMemo, useState } from 'react'
import { Button } from '@bs/ui-components'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { ProfileAside } from '@/features/profile/components/profile-aside'
import { TalentJobCard } from '@/features/jobs/components/talent-job-card'
import { formatLocation, getCities, getCompanies, getLookup, type CityItem, type CompanyItem, type MetadataItem } from '@/services/metadata'
import {
  applyToJob,
  getApplications,
  getInterestedJobs,
  getRecommendedJobs,
  getSavedJobs,
  saveJob,
  unsaveJob,
  withdrawApplication,
  type Application,
  type JobSearchItem,
  type SavedJob,
  WITHDRAW_REASONS,
  declineLead,
} from '@/features/jobs/api'

type Tab = 'interested' | 'recommended' | 'saved' | 'applied'

const tabs: Array<{ value: Tab; label: string }> = [
  { value: 'interested', label: 'Interested in You' },
  { value: 'recommended', label: 'Recommended' },
  { value: 'saved', label: 'Saved' },
  { value: 'applied', label: 'Applied' },
]

/** What every card on this page needs — built once per source, per tab. */
interface CardView {
  key: string
  title: string
  company: string
  companyLogoUrl?: string
  companySlug?: string
  location: string
  workMode: string
  saved: boolean
  applied: boolean
  /** Optional, not always present: a saved/applied row snapshotted before `jobData.slug` existed
   * has no job slug to link to at all (see `realSaved`/`realApplied`) — nothing to fabricate. */
  onOpen?(): void
  onSave?(): void
  onApply?(): void
  onWithdraw?(): void
  onDecline?(): void
}

function locationLabel(cityId: number | undefined, country: string | undefined, citiesById: Record<number, CityItem>): string {
  const city = cityId !== undefined ? citiesById[cityId] : undefined
  return formatLocation(city, country)
}

/** `exactOptionalPropertyTypes` rejects `{logoUrl: undefined}` — spread this in only when present. */
function companyLinkFor(companyId: string, companiesById: Record<string, CompanyItem>) {
  const company = companiesById[companyId]
  return {
    ...(company?.logoUrl && { companyLogoUrl: company.logoUrl }),
    ...(company?.slug && { companySlug: company.slug }),
  }
}

export function JobsPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Carried onto the job-detail Link/navigate below so its own "Back to jobs" returns to THIS
  // tab/search, not a bare `/jobs` that drops which tab was open.
  const backState = { from: `${location.pathname}${location.search}` }
  const [activeTab, setActiveTab] = useState<Tab>('recommended')
  const query = searchParams.get('q')?.toLowerCase() ?? ''

  const [realJobs, setRealJobs] = useState<JobSearchItem[]>([])
  const [realInterestedJobs, setRealInterestedJobs] = useState<JobSearchItem[]>([])
  // Legacy WithdrawJobProcessModal: withdraw and decline both ask for a reason first.
  const [reasonPrompt, setReasonPrompt] = useState<{ kind: 'withdraw'; applicationId: string } | { kind: 'decline'; processId: string; jobId: string } | null>(null)
  const [reasonValue, setReasonValue] = useState('')
  const [reasonBusy, setReasonBusy] = useState(false)
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [workspaceTypes, setWorkspaceTypes] = useState<MetadataItem[]>([])
  const [companiesById, setCompaniesById] = useState<Record<string, CompanyItem>>({})
  const [citiesById, setCitiesById] = useState<Record<number, CityItem>>({})
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getRecommendedJobs({ limit: 50 }),
      getInterestedJobs({ limit: 50 }).catch(() => ({ items: [] as JobSearchItem[] })),
      getSavedJobs().catch(() => ({ items: [] as SavedJob[] })),
      getApplications().catch(() => ({ items: [] as Application[] })),
      getLookup('workspaceTypes'),
    ])
      .then(async ([jobsRes, interestedRes, savedRes, appsRes, workspaceType]) => {
        if (cancelled) return
        const companyIds = Array.from(
          new Set([
            ...jobsRes.items.map((j) => j.companyId),
            ...interestedRes.items.map((j) => j.companyId),
            ...savedRes.items.map((s) => s.companyId),
            ...appsRes.items.map((a) => a.companyId),
          ]),
        )
        // Saved/applied rows carry their OWN jobData/companyData snapshot — no join needed for
        // those, but their cityId is still numeric and needs the same city-name hydration.
        const cityIds = Array.from(
          new Set([
            ...jobsRes.items.flatMap((j) => (j.location?.cityId !== undefined ? [j.location.cityId] : [])),
            ...interestedRes.items.flatMap((j) => (j.location?.cityId !== undefined ? [j.location.cityId] : [])),
            ...savedRes.items.flatMap((s) => (s.jobData?.cityId !== undefined ? [s.jobData.cityId] : [])),
            ...appsRes.items.flatMap((a) => (a.jobData?.cityId !== undefined ? [a.jobData.cityId] : [])),
          ]),
        )
        const [companiesRes, citiesRes] = await Promise.all([
          companyIds.length ? getCompanies({ ids: companyIds }) : Promise.resolve([]),
          cityIds.length ? getCities({ ids: cityIds }) : Promise.resolve([]),
        ])
        if (cancelled) return
        setRealJobs(jobsRes.items)
        setRealInterestedJobs(interestedRes.items)
        setSavedJobs(savedRes.items)
        setApplications(appsRes.items)
        setWorkspaceTypes(workspaceType)
        setCompaniesById(Object.fromEntries(companiesRes.map((c) => [c._id, c])))
        setCitiesById(Object.fromEntries(citiesRes.map((c) => [c.cityId, c])))
      })
      .catch(() => {
        // Surfaced, not swallowed — with no fallback data left, a failed feed rendering the
        // "No jobs here yet" empty state is indistinguishable from having no jobs.
        if (!cancelled) setLoadError('Could not load your jobs — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  function snapshotFor(job: JobSearchItem) {
    const company = companiesById[job.companyId]
    return {
      jobId: job._id,
      companyId: job.companyId,
      companyData: { ...(company?.name && { name: company.name }), ...(company?.logoUrl && { logoUrl: company.logoUrl }) },
      jobData: {
        title: job.title,
        slug: job.slug,
        ...(job.location?.cityId !== undefined && { cityId: job.location.cityId }),
        ...(job.location?.country && { country: job.location.country }),
      },
    }
  }

  async function handleSave(job: JobSearchItem) {
    const already = savedJobs.some((s) => s.jobId === job._id)
    if (already) {
      await unsaveJob(job._id).catch(() => {})
      setSavedJobs((current) => current.filter((s) => s.jobId !== job._id))
      return
    }
    const { savedJob } = await saveJob(snapshotFor(job)).catch(() => ({ savedJob: undefined }))
    if (savedJob) setSavedJobs((current) => [savedJob, ...current])
  }

  async function handleApply(job: JobSearchItem) {
    if (applications.some((a) => a.jobId === job._id && a.status !== 'withdrawn')) return
    const { application } = await applyToJob(snapshotFor(job)).catch(() => ({ application: undefined }))
    if (application) setApplications((current) => [application, ...current])
  }

  function handleWithdraw(applicationId: string) {
    setReasonValue('')
    setReasonPrompt({ kind: 'withdraw', applicationId })
  }

  async function confirmReason() {
    if (!reasonPrompt || reasonBusy) return
    setReasonBusy(true)
    try {
      if (reasonPrompt.kind === 'withdraw') {
        await withdrawApplication(reasonPrompt.applicationId, reasonValue ? { reason: reasonValue } : {}).catch(() => {})
        setApplications((current) => current.filter((a) => a._id !== reasonPrompt.applicationId))
      } else {
        await declineLead(reasonPrompt.processId, reasonValue || undefined)
        setRealInterestedJobs((current) => current.filter((job) => job._id !== reasonPrompt.jobId))
      }
      setReasonPrompt(null)
    } catch {
      // The prompt stays open; the user can retry or cancel.
    } finally {
      setReasonBusy(false)
    }
  }

  const realRecommended: CardView[] = useMemo(
    () =>
      realJobs.map((job) => ({
        key: job._id,
        title: job.title,
        company: companiesById[job.companyId]?.name ?? '',
        ...companyLinkFor(job.companyId, companiesById),
        location: locationLabel(job.location?.cityId, job.location?.country, citiesById),
        workMode: workspaceTypes.find((w) => w.elId === job.workspaceType)?.value ?? '',
        saved: savedJobs.some((s) => s.jobId === job._id),
        applied: applications.some((a) => a.jobId === job._id && a.status !== 'withdrawn'),
        onOpen: () => navigate(`/job/${job.slug}`, { state: backState }),
        onSave: () => void handleSave(job),
        onApply: () => void handleApply(job),
        // Real `match_pairs` rows, not a queue the talent curates — nothing to "decline" out of.
      })),
    [realJobs, companiesById, citiesById, workspaceTypes, savedJobs, applications],
  )

  const realInterested: CardView[] = useMemo(
    () =>
      realInterestedJobs.map((job) => ({
        key: job._id,
        title: job.title,
        company: companiesById[job.companyId]?.name ?? '',
        ...companyLinkFor(job.companyId, companiesById),
        location: locationLabel(job.location?.cityId, job.location?.country, citiesById),
        workMode: workspaceTypes.find((w) => w.elId === job.workspaceType)?.value ?? '',
        saved: savedJobs.some((s) => s.jobId === job._id),
        applied: applications.some((a) => a.jobId === job._id && a.status !== 'withdrawn'),
        onOpen: () => navigate(`/job/${job.slug}`, { state: backState }),
        onSave: () => void handleSave(job),
        onApply: () => void handleApply(job),
        // Legacy JobsContainer `onProcessDecline`: the lead process is withdrawn with a reason.
        ...(job.processId ? { onDecline: () => { setReasonValue(''); setReasonPrompt({ kind: 'decline', processId: job.processId!, jobId: job._id }) } } : {}),
      })),
    [realInterestedJobs, companiesById, citiesById, workspaceTypes, savedJobs, applications],
  )

  const realSaved: CardView[] = useMemo(
    () =>
      savedJobs.map((saved) => ({
        key: saved._id,
        title: saved.jobData?.title ?? '',
        company: companiesById[saved.companyId]?.name ?? saved.companyData?.name ?? '',
        ...companyLinkFor(saved.companyId, companiesById),
        location: locationLabel(saved.jobData?.cityId, saved.jobData?.country, citiesById),
        workMode: '',
        saved: true,
        applied: applications.some((a) => a.jobId === saved.jobId && a.status !== 'withdrawn'),
        // `saved.slug` is this SAVED-JOB ROW's own identifier (`jd`+nanoid) — unrelated to the
        // job's slug the public `/job/:slug` route needs. `jobData.slug` is the actual job slug,
        // snapshotted at save time (see `snapshotFor()`); absent on rows saved before that field
        // existed, in which case there's nothing to link to and the card falls back to non-clickable.
        ...(saved.jobData?.slug && { onOpen: () => navigate(`/job/${saved.jobData?.slug}`, { state: backState }) }),
        onSave: () => {
          void unsaveJob(saved.jobId).catch(() => {})
          setSavedJobs((current) => current.filter((s) => s._id !== saved._id))
        },
      })),
    [savedJobs, applications, citiesById, companiesById, navigate, backState],
  )

  const realApplied: CardView[] = useMemo(
    () =>
      applications
        .filter((application) => application.status !== 'withdrawn')
        .map((application) => ({
          key: application._id,
          title: application.jobData?.title ?? '',
          company: companiesById[application.companyId]?.name ?? application.companyData?.name ?? '',
          ...companyLinkFor(application.companyId, companiesById),
          location: locationLabel(application.jobData?.cityId, application.jobData?.country, citiesById),
          workMode: '',
          saved: false,
          applied: true,
          // `application.slug` is this APPLICATION ROW's own identifier — see the same note on
          // `realSaved` above. `jobData.slug` is the actual job slug.
          ...(application.jobData?.slug && { onOpen: () => navigate(`/job/${application.jobData?.slug}`, { state: backState }) }),
          onWithdraw: () => void handleWithdraw(application._id),
        })),
    [applications, citiesById, companiesById, navigate, backState],
  )


  const realByTab: Record<Tab, CardView[] | null> = {
    interested: realInterested,
    recommended: realRecommended,
    saved: realSaved,
    applied: realApplied,
  }

  // Real rows only. `null` here means this tab has not loaded yet, and an empty list is the honest
  // render for that — the fixture list this used to fall back to looked identical to real results.
  const visibleJobs = (realByTab[activeTab] ?? []).filter(
    (item) => !query || `${item.title} ${item.company} ${item.location}`.toLowerCase().includes(query),
  )

  return (
    <main className="mx-auto max-w-[1460px] px-4 py-12 sm:px-[30px]">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <section className="min-w-0 flex-1">
          <div className="mb-4 overflow-x-auto rounded-t-xl bg-white px-6 pt-4 shadow-[0_0_10px_rgba(0,0,0,0.06)] sm:px-10 sm:pt-6">
            <div className="flex min-w-max gap-6 sm:gap-10">
              {tabs.map((tab) => {
                return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  className={`relative h-[60px] whitespace-nowrap text-sm transition sm:text-base ${
                    activeTab === tab.value
                      ? 'font-semibold text-[#404040] after:absolute after:inset-x-0 after:bottom-0 after:h-1.5 after:bg-accent'
                      : 'font-normal text-[#404040] hover:text-accent'
                  }`}
                >
                  {tab.label}
                </button>
                )
              })}
            </div>
          </div>

          {query ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Showing results for <strong className="text-primary">“{query}”</strong>
            </p>
          ) : null}

          <>
              {loadError ? <p role="alert" className="mb-4 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">{loadError}</p> : null}
              <div className="grid gap-4">
                {visibleJobs.map((job) => (
                  <TalentJobCard
                    key={job.key}
                    title={job.title}
                    company={job.company}
                    location={job.location}
                    workMode={job.workMode}
                    posted=""
                    tags={[]}
                    saved={job.saved}
                    applied={job.applied}
                    {...(job.onOpen ? { onOpen: job.onOpen } : {})}
                    {...(job.companyLogoUrl ? { companyLogoUrl: job.companyLogoUrl } : {})}
                    {...(job.companySlug ? { companySlug: job.companySlug } : {})}
                    {...(job.onSave ? { onSave: job.onSave } : {})}
                    {...(job.onApply ? { onApply: job.onApply } : {})}
                    {...(job.onWithdraw ? { onWithdraw: job.onWithdraw } : {})}
                    {...(job.onDecline ? { onDecline: job.onDecline } : {})}
                  />
                ))}
              </div>

              {visibleJobs.length === 0 && !loadError ? (
                <div className="rounded-xl border border-dashed border-[#c7c7c7] bg-white px-6 py-16 text-center">
                  <h2 className="text-lg font-semibold text-primary">No jobs here yet</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {activeTab === 'interested'
                      ? "No recruiter has flagged interest yet — that's normal, not a broken tab."
                      : 'Try another tab or update your job preferences.'}
                  </p>
                </div>
              ) : null}
            </>
        </section>

        {reasonPrompt ? (

          <div role="dialog" aria-label={reasonPrompt.kind === 'withdraw' ? 'Withdraw application' : 'Decline job'} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">

            <form

              className="w-full max-w-[420px] rounded-2xl bg-white p-6"

              onSubmit={(event) => {

                event.preventDefault()

                void confirmReason()

              }}

            >

              <h2 className="text-lg font-medium text-primary">{reasonPrompt.kind === 'withdraw' ? 'Withdraw your application?' : 'Decline this job?'}</h2>

              <label className="mt-4 block text-sm text-muted-foreground">

                Withdraw reason

                <select aria-label="Withdraw reason" value={reasonValue} onChange={(event) => setReasonValue(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-input-border bg-input px-3 text-base text-[#404040]">

                  <option value="">Prefer not to say</option>

                  {WITHDRAW_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}

                </select>

              </label>

              <div className="mt-5 flex justify-end gap-2">

                <Button type="button" variant="ghost" disabled={reasonBusy} onClick={() => setReasonPrompt(null)}>Cancel</Button>

                <Button type="submit" disabled={reasonBusy}>{reasonBusy ? 'Working…' : reasonPrompt.kind === 'withdraw' ? 'Withdraw' : 'Decline'}</Button>

              </div>

            </form>

          </div>

        ) : null}

        <ProfileAside />
      </div>
    </main>
  )
}
